/**
 * PaymentAgent order-id routing: Gimmonix numeric vs LDS UUID.
 */
import { resolveLdsFinancialState } from "./resolveLdsFinancialState.js";
import { extractLdsOrderFinalPriceIls } from "../extractLdsOrderFinalPriceIls.js";

const NUMERIC_ORDER_ID_RE = /^\d+$/;
const UUID_ORDER_ID_RE =
  /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;

function isDevRuntime() {
  if (typeof import.meta !== "undefined" && import.meta?.env?.DEV) return true;
  if (typeof process !== "undefined" && process?.env?.NODE_ENV) {
    return process.env.NODE_ENV !== "production";
  }
  return false;
}

/**
 * @param {string|null|undefined} orderId
 */
export function resolvePaymentOrderType(orderId) {
  const raw = orderId == null ? "" : String(orderId).trim();
  const isNumericOrder = raw.length > 0 && NUMERIC_ORDER_ID_RE.test(raw);
  const isUuidOrder = raw.length > 0 && UUID_ORDER_ID_RE.test(raw);
  const isLikelyGimmonixOrder = isNumericOrder;
  const isLikelyLdsOrder = isUuidOrder;

  return {
    isNumericOrder,
    isUuidOrder,
    isLikelyLdsOrder,
    isLikelyGimmonixOrder,
    isNewOrder: raw === "newOrder",
    isLoadable: isNumericOrder || isUuidOrder || raw === "newOrder",
  };
}

/**
 * LDS row validation for PaymentAgent (not Gimmonix segments/package shape).
 * @returns {string|null} error reason key or null if ok
 */
export function validateLdsPaymentAgentOrder(orderData) {
  if (!orderData || typeof orderData !== "object") return "missing_order";
  if (orderData.status === "fail") return "order_not_found";
  if (
    orderData.error &&
    typeof orderData.error === "string" &&
    orderData.error.length > 0 &&
    !orderData.id &&
    !orderData.orderid
  ) {
    return "order_not_found";
  }

  const financial = resolveLdsFinancialState(orderData);
  if (financial.totalAmount != null && financial.totalAmount > 0) {
    return null;
  }

  const fallbackIls = extractLdsOrderFinalPriceIls(orderData);
  if (fallbackIls != null && fallbackIls > 0) {
    return null;
  }

  const our = orderData.ourprice || orderData.ourPrice;
  const direct =
    Number(our?.priceWithCreditCardFee) ||
    Number(our?.priceWithCommissionAndCreditCardFee) ||
    Number(our?.ourTotalFare?.priceWithCommissionAndCreditCardFee) ||
    0;
  if (Number.isFinite(direct) && direct > 0) {
    return null;
  }

  return "missing_total";
}

/**
 * DEV-only routing trace for PaymentAgent mount/load decisions.
 */
export function logPaymentAgentOrderRoutingTrace(payload) {
  if (!isDevRuntime()) return;
  try {
    // eslint-disable-next-line no-console
    console.info("[PAYMENT_AGENT_ORDER_ROUTING_TRACE]", payload);
  } catch {
    /* ignore */
  }
}

export { NUMERIC_ORDER_ID_RE, UUID_ORDER_ID_RE };
